/**
 * #592 follow-up — make the protocol optional on user-entered URLs.
 *
 * Onboarding URL fields (hotel website, affiliate asset links) used to fail
 * validation unless the user typed `http(s)://`. This prepends `https://`
 * when a non-empty value has no scheme, so "example.com" validates as
 * "https://example.com". Empty values are returned untouched (so a required
 * field still fails the "required" check rather than becoming a bare scheme).
 */
export function ensureUrlProtocol(value: string | null | undefined): string {
  const trimmed = (value ?? '').trim()
  if (!trimmed) return trimmed
  // Already has a scheme (http://, https://, ftp://, mailto:, etc.) — leave it.
  if (/^[a-zA-Z][a-zA-Z\d+.-]*:\/\//.test(trimmed) || /^mailto:/i.test(trimmed)) {
    return trimmed
  }
  return `https://${trimmed}`
}

/**
 * #2174 — is a stored affiliation link something an affiliate can publish?
 * Only an absolute http(s) URL counts. Airtable's placeholder text
 * ("waiting for approval") and other free text are never shown as a link.
 */
export function isUsableLinkUrl(value: string | null | undefined): boolean {
  return typeof value === 'string' && /^https?:\/\/\S+$/i.test(value.trim())
}

/** #2174 — keep only the entries whose url is usable (see isUsableLinkUrl). */
export function usableLinks<T extends { url?: string | null }>(links: T[] | null | undefined): T[] {
  return (links ?? []).filter((l) => isUsableLinkUrl(l?.url))
}

/**
 * #284 — is this path one of the white-label embed surfaces rendered inside a
 * hotel's <iframe>?
 *
 * Chrome-level overlays (the cookie banner) must not render there: the iframe is
 * short, so a fixed-position banner sits directly on top of the terms checkbox
 * and the Submit button and the applicant can never finish the form. The host
 * page carries its own cookie notice.
 */
export function isEmbedPath(pathname: string | null | undefined): boolean {
  return (pathname ?? '').startsWith('/embed/')
}
